import {
  FormHelperText,
  CheckboxProps,
  Checkbox,
  FormControlLabel,
  FormGroup,
  useTheme,
} from '@mui/material';
import { useField } from 'formik';
import { ReactNode } from 'react';
import { CheckedIcon, DisabledCheckedIcon, DisabledUncheckedIcon, UncheckedIcon } from '../../assets/SvgIcons';
import { useFieldRequired } from './FormSchemaContext';

export const MFCheckbox = function ({
  label,
  name,
  onChange,
  checked,
  onKeyDown,
  sx,
  ...props
}: CheckboxProps & { label: string | JSX.Element; name: string; onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>; }): JSX.Element {
  const [field, meta] = useField(name);
  checked = checked || field.value;
  onChange = onChange || field.onChange;
  const theme = useTheme();
  const errorText = meta.error && meta.touched ? meta.error : '';
  const isRequired = useFieldRequired(name);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();

      const newChecked = !checked;

      const syntheticEvent = {
        target: {
          name,
          checked: newChecked,
          value: newChecked,
        },
      } as unknown as React.ChangeEvent<HTMLInputElement>;

      onChange?.(syntheticEvent, newChecked);
    }
  };

  const handleCombinedKeyDown: React.KeyboardEventHandler<HTMLButtonElement> = (event) => {
    onKeyDown?.(event);

    if (event.defaultPrevented) return;

    handleKeyDown(event);
  };

  return (
    <FormGroup>
      <FormControlLabel
        control={
          <Checkbox
            checkedIcon={props?.disabled ? <DisabledCheckedIcon /> : <CheckedIcon />}
            icon={props?.disabled ? <DisabledUncheckedIcon /> : <UncheckedIcon />}
            {...field}
            {...props}
            checked={checked}
            onChange={onChange}
            onKeyDown={handleCombinedKeyDown}
            inputProps={{
              'aria-required': isRequired || undefined,
              ...(props.inputProps || {}),
            }}
          />}
        name={name}
        label={label}
        sx={{
          '& .Mui-disabled': {
            cursor: !props.disabled ? 'pointer' : 'not-allowed !important',
            pointerEvents: 'all',
          },
          ...sx,
        }}
        componentsProps={{ typography: { variant: 'body_500' } }}
      />
      <FormHelperText error sx={{ marginLeft: 'unset', color: `${theme.palette.error[900]}`, fontWeight: 500 }}>
        {errorText}
      </FormHelperText>
    </FormGroup>
  );
};

export default MFCheckbox;
